<script setup>
import {ref, computed} from 'vue';
import CustomToggle from '@/components/global-themed/CustomToggle.vue';
import DefaultButton from '@/components/global-themed/buttons/DefaultButton.vue';
import DeleteBannerButton from '@/components/admin/banners/DeleteBannerButton.vue';
import BaseModal from '@/components/global-themed/BaseModal.vue';
import {useCreateBanner, useUpdateBanner} from '@/composables/admin/useAdminBanners.js';
import {errorToast, successToast} from '@/composables/useToasts';
import {BANNER_IMAGE_SIZES} from '@/composables/banners/bannerViewportSizes.js';

const props = defineProps({
    banner: {
        type: Object,
        default: null,
    },
});

const isEditMode = computed(() => props.banner !== null);

const form = ref({
    title: props.banner?.title ?? '',
    cta_url: props.banner?.cta_url ?? '',
    active: props.banner?.active ?? false,
    currency: props.banner?.currency ?? '',
    fallback_images: props.banner?.fallback_images ?? true,
});

const originalImages = {
    large: props.banner?.images?.large ?? null,
    medium: props.banner?.images?.medium ?? null,
    small: props.banner?.images?.small ?? null,
};

const pendingFiles = ref({large: null, medium: null, small: null});
const pendingPreviews = ref({large: null, medium: null, small: null});
const markedForDeletion = ref({large: false, medium: false, small: false});

const errors = ref({});
const showInactiveConfirmModal = ref(false);

const {mutateAsync: createBanner, isPending: isCreating} = useCreateBanner();
const {mutateAsync: updateBanner, isPending: isUpdating} = useUpdateBanner();

const isSaving = computed(() => isCreating.value || isUpdating.value);

function previewFor(size) {
    if (pendingPreviews.value[size]) {
        return pendingPreviews.value[size];
    }
    if (markedForDeletion.value[size]) {
        return null;
    }
    return originalImages[size];
}

function buildFormData() {
    const formData = new FormData();
    formData.append('title', form.value.title);
    formData.append('active', form.value.active ? '1' : '0');
    formData.append('fallback_images', form.value.fallback_images ? '1' : '0');

    if (form.value.cta_url) {
        formData.append('cta_url', form.value.cta_url);
    }

    if (form.value.currency) {
        formData.append('currency', form.value.currency);
    }

    for (const {size} of BANNER_IMAGE_SIZES) {
        if (pendingFiles.value[size] !== null) {
            formData.append(size, pendingFiles.value[size]);
        } else if (isEditMode.value && markedForDeletion.value[size]) {
            formData.append(`remove_${size}`, '1');
        }
    }

    return formData;
}

function hasAtLeastOneImage() {
    return BANNER_IMAGE_SIZES.some(({size}) => {
        if (pendingFiles.value[size] !== null) {
            return true;
        }

        if (markedForDeletion.value[size]) {
            return false;
        }

        return originalImages[size] !== null;
    });
}

function firstValidationErrorMessage(validationErrors) {
    const messages = Object.values(validationErrors).flat();

    return messages[0] ?? 'Please fix the validation errors below.';
}

async function submitSave(activate = null) {
    if (activate === true) {
        form.value.active = true;
    }

    showInactiveConfirmModal.value = false;
    errors.value = {};

    try {
        if (isEditMode.value) {
            await updateBanner({id: props.banner.id, payload: buildFormData()});
            successToast('Banner updated successfully.');
        } else {
            await createBanner(buildFormData());
            successToast('Banner created successfully.');
        }

        setTimeout(() => {
            window.location.href = '/admin/banners';
        }, 1500);
    } catch (err) {
        if (err.response?.status === 422 && err.response?.data?.errors) {
            errors.value = err.response.data.errors;
            errorToast(firstValidationErrorMessage(err.response.data.errors));

            return;
        }

        errorToast('Something went wrong saving the banner.');
    }
}

function closeInactiveConfirmModal() {
    showInactiveConfirmModal.value = false;
}

async function save() {
    errors.value = {};

    if (!hasAtLeastOneImage()) {
        errors.value = {images: ['At least one banner image is required.']};
        errorToast('At least one banner image is required.');
        return;
    }

    if (!isEditMode.value && !form.value.active) {
        showInactiveConfirmModal.value = true;
        return;
    }

    await submitSave();
}

function handleImageSelect(size, event) {
    const file = event.target.files?.[0];
    if (!file) {
        return;
    }

    if (pendingPreviews.value[size]) {
        URL.revokeObjectURL(pendingPreviews.value[size]);
    }

    pendingFiles.value[size] = file;
    pendingPreviews.value[size] = URL.createObjectURL(file);
    markedForDeletion.value[size] = false;
    event.target.value = '';
}

function handleRemoveImage(size) {
    if (pendingPreviews.value[size]) {
        URL.revokeObjectURL(pendingPreviews.value[size]);
    }

    pendingFiles.value[size] = null;
    pendingPreviews.value[size] = null;

    if (originalImages[size]) {
        markedForDeletion.value[size] = true;
    }
}

function handleBannerDeleted() {
    setTimeout(() => {
        window.location.href = '/admin/banners';
    }, 1500);
}
</script>

<template>
  <div class="tw-max-w-default-container tw-mx-auto tw-px-6 tw-py-2">
    <div class="tw-flex tw-items-center tw-justify-between tw-mb-8">
      <h1 class="tw-font-bold tw-text-4.5xl tw-text-neutral-900">
        {{ isEditMode ? 'Edit Banner' : 'Create Banner' }}
      </h1>

      <DefaultButton
        variant="primary-inverted"
        size="small"
        as="a"
        href="/admin/banners"
      >
        <svg-vue
          icon="icons/back-arrow"
          class="tw-w-4 tw-h-4 tw-mr-2"
        />
        <span class="tw-font-semibold tw-antialiased">Back to Banners</span>
      </DefaultButton>
    </div>

    <div class="tw-bg-white tw-rounded-xl tw-shadow-sm tw-p-6">
      <div class="tw-flex tw-flex-col tw-gap-6">
        <div>
          <label class="tw-block tw-text-sm tw-font-medium tw-mb-1">
            Title <span class="tw-text-red-500">*</span>
          </label>
          <input
            v-model="form.title"
            type="text"
            class="tw-w-full tw-border tw-border-grey-light tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary"
            placeholder="Banner title"
          >
          <p
            v-if="errors.title"
            class="tw-text-red-500 tw-text-xs tw-mt-1"
          >
            {{ errors.title[0] }}
          </p>
        </div>

        <div>
          <label class="tw-block tw-text-sm tw-font-medium tw-mb-1">
            CTA URL <span class="tw-text-grey-dark tw-font-normal">(optional)</span>
          </label>
          <input
            v-model="form.cta_url"
            type="url"
            class="tw-w-full tw-border tw-border-grey-light tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary"
            placeholder="https://example.com"
          >
          <p
            v-if="errors.cta_url"
            class="tw-text-red-500 tw-text-xs tw-mt-1"
          >
            {{ errors.cta_url[0] }}
          </p>
        </div>

        <div>
          <label class="tw-block tw-text-sm tw-font-medium tw-mb-1">
            Currency <span class="tw-text-grey-dark tw-font-normal">(optional)</span>
          </label>
          <select
            v-model="form.currency"
            class="tw-w-full tw-border tw-border-grey-light tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary"
          >
            <option value="">
              All currencies
            </option>
            <option value="GBP">
              GBP
            </option>
            <option value="USD">
              USD
            </option>
          </select>
          <p
            v-if="errors.currency"
            class="tw-text-red-500 tw-text-xs tw-mt-1"
          >
            {{ errors.currency[0] }}
          </p>
        </div>

        <div class="tw-flex tw-items-center tw-gap-3">
          <label class="tw-text-sm tw-font-medium">Active</label>
          <CustomToggle v-model="form.active" />
        </div>

        <div class="tw-border-t tw-border-grey-light tw-pt-6">
          <h2 class="tw-text-base tw-font-semibold tw-mb-1">
            Images
          </h2>
          <p class="tw-text-xs tw-text-grey-dark tw-mb-5">
            Each size is optional. At least one image is required.
            Image changes are saved when you save the banner.
          </p>

          <p
            v-if="errors.images"
            class="tw-text-red-500 tw-text-xs tw-mb-4"
          >
            {{ errors.images[0] }}
          </p>

          <div class="tw-flex tw-flex-col tw-gap-4">
            <div
              v-for="item in BANNER_IMAGE_SIZES"
              :key="item.size"
              class="tw-border tw-border-grey-light tw-rounded-lg tw-p-4 tw-w-full"
            >
              <p class="tw-text-sm tw-font-medium tw-mb-3">
                {{ item.label }}
              </p>

              <div
                v-if="previewFor(item.size)"
                class="tw-flex tw-flex-col tw-items-center tw-mb-3"
              >
                <img
                  :src="previewFor(item.size)"
                  :alt="item.label"
                  class="tw-max-w-full tw-h-auto tw-rounded tw-mb-2"
                >
                <DefaultButton
                  variant="link"
                  size="small"
                  class="!tw-text-danger"
                  @click="handleRemoveImage(item.size)"
                >
                  Remove
                </DefaultButton>
              </div>

              <label
                :for="`image-${item.size}`"
                class="tw-block"
              >
                <span class="tw-text-xs tw-text-grey-dark tw-block tw-mb-1">
                  {{ previewFor(item.size) ? 'Replace image' : 'Choose image' }}
                </span>
                <input
                  :id="`image-${item.size}`"
                  type="file"
                  accept="image/*"
                  class="tw-text-xs tw-text-grey-dark tw-w-full"
                  @change="handleImageSelect(item.size, $event)"
                >
              </label>
            </div>
          </div>
        </div>

        <div class="tw-flex tw-flex-col tw-gap-2">
          <div class="tw-flex tw-items-center tw-gap-3">
            <label class="tw-text-sm tw-font-medium">Fallback Images</label>
            <CustomToggle v-model="form.fallback_images" />
          </div>
          <p class="tw-text-xs tw-text-grey-dark">
            When enabled, the display uses the next available image size if the preferred size for a viewport is missing.
          </p>
          <p class="tw-text-xs tw-text-grey-dark">
            When disabled, only images uploaded for the matching viewport size are shown.
          </p>
        </div>

        <div class="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-pt-2">
          <div class="tw-flex tw-gap-3">
            <DefaultButton
              variant="primary-inverted"
              size="small"
              as="a"
              href="/admin/banners"
            >
              Cancel
            </DefaultButton>

            <DeleteBannerButton
              v-if="isEditMode"
              button-class="!tw-py-2 !tw-px-4 !tw-rounded-lg"
              :banner="banner"
              @deleted="handleBannerDeleted"
            />
          </div>

          <DefaultButton
            variant="primary"
            size="small"
            :loading="isSaving"
            :disabled="isSaving"
            @click="save"
          >
            {{ isSaving ? 'Saving...' : (isEditMode ? 'Save Changes' : 'Create Banner') }}
          </DefaultButton>
        </div>
      </div>
    </div>
  </div>

  <Teleport to=".tailwind">
    <BaseModal
      v-if="showInactiveConfirmModal"
      modal-class="tw-max-w-md"
      @close="closeInactiveConfirmModal"
    >
      <div>
        <h2 class="tw-text-2xl tw-text-center tw-font-bold">
          Save banner as inactive?
        </h2>

        <p class="tw-my-6 tw-text-center tw-text-sm tw-text-grey-dark">
          This banner will not appear on the dashboard until it is activated. Would you like to save it now or save and activate it?
        </p>

        <div class="tw-flex tw-flex-col tw-gap-2">
          <DefaultButton
            variant="primary"
            :loading="isSaving"
            :disabled="isSaving"
            @click="submitSave(true)"
          >
            {{ isSaving ? 'Saving...' : 'Save and Activate' }}
          </DefaultButton>

          <DefaultButton
            variant="primary-inverted"
            :loading="isSaving"
            :disabled="isSaving"
            @click="submitSave()"
          >
            {{ isSaving ? 'Saving...' : 'Save' }}
          </DefaultButton>

          <DefaultButton
            variant="base"
            :disabled="isSaving"
            @click="closeInactiveConfirmModal"
          >
            Cancel
          </DefaultButton>
        </div>
      </div>
    </BaseModal>
  </Teleport>
</template>
